function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, forwardRef } from 'react';
import cn from 'clsx';
import { string, oneOf, bool, arrayOf, shape, oneOfType, number, func, any } from 'prop-types';
import FormContext from '../../context/FormContext.js';
import FormGroupContext from '../../context/FormGroupContext.js';
import FormLabel from '../FormLabel/index.jsx';

/**
 * FormInput component.
 */
const FormInput = /*#__PURE__*/forwardRef(({
  placeholder,
  defaultValue,
  type,
  className,
  borderless,
  name,
  as,
  options,
  hasError,
  onChange,
  onClick,
  onKeyDown,
  value,
  inlineLabel,
  inlineLabelSelected,
  ...restProps
}, ref) => {
  const {
    inputId,
    feedbackId
  } = useContext(FormGroupContext);
  const {
    inverted,
    errors,
    validateInput,
    mobileSquareBorders,
    onChange: onFormChange,
    defaultValues
  } = useContext(FormContext);
  const handleChange = evt => {
    if (onChange) {
      onChange(evt);
    }
    if (onFormChange) {
      onFormChange(evt);
    }
  };
  const Component = as || 'input';
  const selectOptions = options.length ? options.map(child => /*#__PURE__*/React.createElement("option", {
    key: child.value,
    value: child.value,
    disabled: child.disabled,
    "data-item-name": child.text
  }, child.text)) : null;

  // Use value for a controlled and defaultValue for an uncontrolled element.
  // https://reactjs.org/docs/forms.html#controlled-components
  const valueProp = value !== null ? {
    value
  } : {
    defaultValue: defaultValue || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name]) || ''
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Component, _extends({}, restProps, valueProp, {
    className: cn('form-input', (hasError || (errors === null || errors === void 0 ? void 0 : errors[name])) && 'form-input--invalid', className, inverted && 'form-input--inverted', borderless && 'form-input--borderless', mobileSquareBorders && 'form-input--mobile-square-borders', !!inlineLabel && 'form-input--with-label', {
      'select': 'form-input--dropdown',
      'textarea': 'form-input--height-auto'
    }[as]),
    id: inputId,
    type: type,
    placeholder: placeholder || ' ',
    name: name,
    ref: ref,
    onChange: validateInput ? evt => validateInput(evt, handleChange) : handleChange,
    onClick: onClick,
    onKeyDown: onKeyDown,
    "aria-describedby": feedbackId,
    "aria-invalid": hasError || !!(errors === null || errors === void 0 ? void 0 : errors[name])
  }), selectOptions), inlineLabel && /*#__PURE__*/React.createElement(FormLabel, {
    className: 'form-input__inline-label p-absolute' + (!!inlineLabelSelected ? " form-input__inline-label--selected" : ""),
    htmlFor: inputId
  }, inlineLabel));
});
FormInput.displayName = 'FormInput';
FormInput.defaultProps = {
  placeholder: null,
  defaultValue: '',
  type: 'text',
  className: null,
  borderless: false,
  name: null,
  options: [],
  as: null,
  hasError: false,
  onChange: null,
  onClick: null,
  onKeyDown: null,
  value: null,
  inlineLabel: null,
  inlineLabelSelected: null
};
FormInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The input field placeholder */
  placeholder: string,
  /** The default value for the input field. */
  defaultValue: any,
  /** The input field type */
  type: oneOf(['color', 'date', 'datetime-local', 'email', 'file', 'hidden', 'month', 'number', 'password', 'search', 'tel', 'text', 'url', 'week']),
  /** Remove the borders from the input, primarily for when used as part of FormInputGroup. */
  borderless: bool,
  /** The input name. */
  name: oneOfType([number, string]),
  /** Options for a select input. */
  options: arrayOf(shape({
    /** The value of the option  */
    value: oneOfType([string, number]),
    /** The text of the option that appears on the screen */
    text: string,
    /** Optional property to show which dropdown option is selected */
    selected: bool
  })),
  /** Render with an alternative root tag (e.g. an <select> instead of a <input>). */
  as: string,
  /** Mark the form input as invalid. Note that the input will be marked as
    invalid if the errors object in the form context contains a relevant error. */
  hasError: bool,
  /** On change event handler. */
  onChange: func,
  /** On click event handler. */
  onClick: func,
  /** On key down event handler. */
  onKeyDown: func,
  /** The value for when using as a controlled component. */
  value: any,
  /** Inline label - if not set it would take the default style */
  inlineLabel: string,
  /** Adds an additional style to indicate selected values - see FormMultiSelect */
  inlineLabelSelected: bool
} : {};
export default FormInput;